import * as React from "react";
import {useNavigate} from "react-router-dom";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {AppBar, Card, TextField, Button, Alert, Snackbar} from "@mui/material";
import properties from "constants/ApplicationConstants";
import {POST} from "APIclient"; // Adjust path to your POST utility

export default function AddYoutubeLinks() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [youtubeLink, setYoutubeLink] = React.useState("");
  const [formErrors, setFormErrors] = React.useState({});
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [snakColor, setSnakColor] = React.useState("success");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [open, setOpen] = React.useState(false);

  const validateField = (key, value) => {
    if (key === "youtubeLink") {
      if (!value.trim()) {
        return "YouTube link is required.";
      }
      if (!isValidUrl(value)) {
        return "Please enter a valid YouTube URL.";
      }
    }
    return null;
  };

  const isValidUrl = url => {
    try {
      new URL(url);
      return url.startsWith("https://www.youtube.com/") || url.startsWith("https://youtu.be/");
    } catch {
      return false;
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const newErrors = {};
    const fields = {youtubeLink};
    Object.keys(fields).forEach(key => {
      const error = validateField(key, fields[key]);
      if (error) newErrors[key] = error;
    });

    setFormErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setSaveLoading(true);
    setSnakMessage(null);

    const payload = {youtubeLink};

    try {
      const response = await POST(`admin/add-youtube-link`, payload);
      if (response?.status === 200) {
        setSnakColor("success");
        setSnakMessage(response.data.message || "YouTube link added successfully.");
        setOpen(true);
        setYoutubeLink("");
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      const message = error?.response?.data?.message || "Failed to add YouTube link.";

      if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage("Bad request. Please check your inputs.");
      } else if (statusCode === 401) {
        setSnakColor("error");
        setSnakMessage("Unauthorized access. Please log in again.");
      } else if (statusCode === 403) {
        setSnakColor("error");
        setSnakMessage("Forbidden. You don’t have permission to perform this action.");
      } else if (statusCode === 404) {
        setSnakColor("error");
        setSnakMessage("API not found. Please contact support.");
      } else if (statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("YouTube link already exists.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(message);
      }
      setOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox m={2}>
        <AppBar position="static">
          <Card>
            <MDBox pt={4} px={3} pb={4} sx={{borderRadius: "10px", border: "1px solid #D3D3D3"}}>
              <MDTypography
                variant="h4"
                sx={{
                  textAlign: "center",
                  fontFamily: properties.fontFamily,
                  mb: 4
                }}
              >
                Add YouTube Link
              </MDTypography>
              <Box component="form" onSubmit={handleSubmit} sx={{maxWidth: 600, mx: "auto"}}>
                <TextField
                  fullWidth
                  label="YouTube Link"
                  value={youtubeLink}
                  onChange={e => setYoutubeLink(e.target.value)}
                  variant="outlined"
                  error={!!formErrors.youtubeLink}
                  helperText={
                    formErrors.youtubeLink ||
                    "Enter a valid YouTube URL (e.g., https://www.youtube.com/watch?v=example)"
                  }
                  sx={{mb: 3}}
                  disabled={saveLoading}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{
                    width: "200px",
                    py: 1.5,
                    fontFamily: properties.fontFamily,
                    background: properties.colors.bgcolorbluethem,
                    color: "#fff",
                    mx: "auto",
                    display: "block"
                  }}
                  disabled={saveLoading}
                >
                  {saveLoading ? "Submitting..." : "Submit"}
                </Button>
              </Box>
            </MDBox>
          </Card>
        </AppBar>
        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
        >
          <Alert onClose={handleClose} severity={snakColor} sx={{width: "100%"}}>
            {snakMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}
